import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type SearchResultItem, useSearch } from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import styles from './CommandPalette.module.css';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_LABEL: Record<string, string> = {
  contact: 'Contacten',
  document: 'Documenten',
  decision: 'Beslissingen',
  task: 'Taken',
  meerwerk: 'Meerwerken',
  invoice: 'Facturen',
  photo: "Foto's",
  werfverslag: 'Werfverslagen',
};

function routeFor(item: SearchResultItem): string {
  // Map a typed search hit to a route. New entity types added on the backend
  // fall through to a sensible default rather than throwing.
  switch (item.type.toLowerCase()) {
    case 'contact':
      return `/contacten/${item.id}`;
    case 'document':
      return `/documenten/${item.id}`;
    case 'decision':
      return '/beslissingen';
    case 'task':
      return '/taken';
    case 'meerwerk':
      return '/meerwerken';
    case 'invoice':
      return `/budget/factuur/${item.id}`;
    case 'photo':
      return '/fotos';
    case 'werfverslag':
      return '/werfverslagen';
    default:
      return '/';
  }
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  // Wrap the search-driving child in the Dialog so useSearch only mounts
  // when the palette is open. This keeps unrelated unit tests — which mock
  // @/api/hooks without `useSearch` — green and avoids issuing a network
  // request on every page load.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <CommandPaletteBody onClose={() => onOpenChange(false)} />}
    </Dialog>
  );
}

function CommandPaletteBody({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const searchQuery = useSearch(query, true);
  const items: SearchResultItem[] = useMemo(
    () => (Array.isArray(searchQuery.data) ? searchQuery.data : []),
    [searchQuery.data],
  );

  // Group hits by type for the grouped-result render. Within each group we
  // keep backend order so the user can rely on stable ranking.
  const grouped = useMemo(() => {
    const map = new Map<string, SearchResultItem[]>();
    for (const item of items) {
      const key = item.type.toLowerCase();
      const bucket = map.get(key);
      if (bucket) bucket.push(item);
      else map.set(key, [item]);
    }
    return Array.from(map.entries());
  }, [items]);

  // Flatten grouped order so keyboard nav matches the visual order even when
  // results span multiple sections.
  const flat = useMemo(() => grouped.flatMap(([, list]) => list), [grouped]);

  const handleSelect = (item: SearchResultItem) => {
    onClose();
    setQuery('');
    navigate(routeFor(item));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[activeIndex];
      if (item) handleSelect(item);
    }
  };

  // Adjust activeIndex if the flat list shrinks beneath it (search changed
  // while focused on a now-removed item).
  useEffect(() => {
    if (activeIndex >= flat.length) setActiveIndex(Math.max(0, flat.length - 1));
  }, [activeIndex, flat.length]);

  const hasMinimumQuery = query.trim().length >= 2;
  const showEmptyHint = !hasMinimumQuery;
  const showNoResults = hasMinimumQuery && !searchQuery.isFetching && grouped.length === 0;

  return (
    <DialogContent width={640} className={styles.palette}>
      <DialogTitle className="sr-only">Zoeken</DialogTitle>
      <DialogDescription className="sr-only">
        Zoek door contacten, documenten, beslissingen, taken, meerwerken, facturen, foto's en
        werfverslagen.
      </DialogDescription>
      <div className={`row gap-10 ${styles.searchRow}`}>
        <Icon name="search" size={16} color="var(--ink-2)" />
        {/* biome-ignore lint/a11y/noAutofocus: a command palette is the canonical case for autoFocus */}
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Zoek…"
          aria-label="Zoekterm"
          className={styles.input}
        />
      </div>
      <div className={styles.results} role="listbox" aria-label="Zoekresultaten">
        {showEmptyHint && <div className={styles.hint}>Type minstens 2 tekens om te zoeken.</div>}
        {showNoResults && <div className={styles.hint}>Geen resultaten gevonden.</div>}
        {grouped.map(([type, list]) => (
          <div key={type} className={styles.group}>
            <div className={`eyebrow ${styles.groupHead}`}>{TYPE_LABEL[type] ?? type}</div>
            {list.map((item) => {
              const idx = flat.indexOf(item);
              const active = idx === activeIndex;
              return (
                <button
                  type="button"
                  key={`${item.type}-${item.id}`}
                  role="option"
                  aria-selected={active}
                  className={`${styles.item} ${active ? styles.itemActive : ''}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <div className={styles.itemTitle}>{item.title}</div>
                  {item.subtitle && <div className={styles.itemSub}>{item.subtitle}</div>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className={`row gap-12 ${styles.footer}`}>
        <span>
          <kbd>↑↓</kbd> navigeer
        </span>
        <span>
          <kbd>↵</kbd> open
        </span>
        <span>
          <kbd>Esc</kbd> sluiten
        </span>
      </div>
    </DialogContent>
  );
}
